import type { FC } from 'react';
import { useEffect } from 'react';

import { createHighlighter } from './studio-highlight';
import { COMLINK_DOMAIN, SYNC_DOMAIN, isInViewport, readMarks, resolveMark } from './studio-sync';

/**
 * How long Sanity gets to scroll before this decides it has not.
 *
 * Sanity smooth-scrolls to an element it matches exactly, which takes a few hundred milliseconds.
 * Checking sooner would see a block still off screen and start a second scroll that fights the first.
 * Waiting out the animation means: if the block is in view by then, someone already took us there.
 */
const SCROLL_GRACE_MS = 700;

export interface StudioSyncProps {
  /** Origin of the Studio. Messages from anywhere else are ignored. */
  studioOrigin: string;
}

const isObject = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null;

const readTarget = (data: unknown): { id: string; path: string } | undefined =>
  isObject(data) && typeof data.id === 'string' && typeof data.path === 'string'
    ? { id: data.id, path: data.path }
    : undefined;

/**
 * Keeps the page in step with where the Studio is pointing, for the cases Sanity does not cover.
 *
 * **Focus.** Sanity highlights and scrolls to an element whose path equals the focused field's exactly.
 * A field with no element of its own — a colour, a toggle, anything not rendered as text — matches
 * nothing and leaves the editor looking at an unchanged page. This outlines the block that owns it and,
 * if Sanity has not already brought it into view, scrolls there. When a mark matches exactly it does
 * nothing, because Sanity is already doing both and a second outline would only fight it.
 *
 * **Hover.** The protocol between the Studio and the overlay has no hover message at all. The Studio
 * side posts one of ours (see "Studio and preview in step" in `docs/sanity-visual-editing.md`), and this outlines the matching
 * element, or the block that contains the field.
 *
 * Every message is checked twice before it is read: it must come from the window that embeds this page,
 * and from the Studio's own origin. The most either message can do is draw an outline and, for focus, scroll
 * the page to a block — nothing is evaluated, navigated or written — but a page that obeys any sibling
 * frame is not one worth building on.
 *
 * Renders nothing, and only runs in draft mode (the render host mounts it beside the overlay).
 */
export const StudioSync: FC<StudioSyncProps> = ({ studioOrigin }) => {
  useEffect(() => {
    const focus = createHighlighter('focus');
    const hover = createHighlighter('hover');
    let scrollTimer: ReturnType<typeof setTimeout> | undefined;

    const clearFocus = (): void => {
      clearTimeout(scrollTimer);
      scrollTimer = undefined;
      focus.hide();
    };

    const onFocus = ({ id, path }: { id: string; path: string }): void => {
      clearFocus();
      const resolved = resolveMark(readMarks(), id, path);
      // No mark contains it, or one is exactly it and Sanity has it covered.
      if (!resolved || resolved.isExact) return;

      const { element } = resolved.mark;
      focus.show(element);
      scrollTimer = setTimeout(() => {
        scrollTimer = undefined;
        if (!isInViewport(element)) element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, SCROLL_GRACE_MS);
    };

    const onHover = ({ id, path }: { id: string; path: string }): void => {
      const resolved = resolveMark(readMarks(), id, path);
      if (resolved) hover.show(resolved.mark.element);
      else hover.hide();
    };

    const onMessage = (event: MessageEvent): void => {
      if (event.source !== window.parent || event.origin !== studioOrigin) return;
      const message: unknown = event.data;
      if (!isObject(message)) return;

      if (message.domain === COMLINK_DOMAIN) {
        if (message.type === 'presentation/blur') clearFocus();
        if (message.type === 'presentation/focus') {
          const target = readTarget(message.data);
          if (target) onFocus(target);
        }
        return;
      }

      if (message.domain === SYNC_DOMAIN) {
        if (message.type === 'hover-end') hover.hide();
        if (message.type === 'hover') {
          const target = readTarget(message);
          if (target) onHover(target);
        }
      }
    };

    window.addEventListener('message', onMessage);
    return () => {
      window.removeEventListener('message', onMessage);
      clearFocus();
      hover.hide();
    };
  }, [studioOrigin]);

  return null;
};
